import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, CalendarDays, Sparkles, Clock, KeyRound, Users, Upload, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";
import {
    TASK_STATE_LABELS,
    TASK_TYPE_LABELS,
    PRIORITY_LABELS,
} from "@/lib/dictionaries";
import { useAuth } from "@/contexts/AuthContext";

const todayISO = () => new Date().toISOString().slice(0, 10);

const KEY_ACCESS_OPTIONS = ["", "Llaves dentro", "Llaves cajetín", "Llaves recepción", "No hay llaves"];
const SERVICE_TYPE_OPTIONS = ["", "CON ENTRADA", "SIN ENTRADA"];

export default function Cleanings() {
    const { meta } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [properties, setProperties] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [dateFilter, setDateFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [form, setForm] = useState({
        property_id: "",
        type: "checkout",
        priority: "medium",
        scheduled_date: todayISO(),
        estimated_minutes: 90,
        assigned_to: "",
        assigned_to_secondary: "",
        start_time: "",
        end_time: "",
        key_access: "",
        service_type_text: "",
        day_instructions: "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const load = () => {
        setLoading(true);
        api.get("/tasks").then((r) => setTasks(r.data || [])).finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        api.get("/properties").then((r) => setProperties(r.data || []));
        api.get("/employees").then((r) => setEmployees(r.data || []));
    }, []);

    const propMap = useMemo(() => Object.fromEntries(properties.map((p) => [p.id, p])), [properties]);
    const empMap = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);

    const [sortMode, setSortMode] = useState("smart"); // smart | date_asc | date_desc

    const todayIso = todayISO();

    const sorted = useMemo(() => {
        const arr = tasks.filter((t) => {
            if (filter !== "all" && t.state !== filter) return false;
            if (dateFilter && t.scheduled_date !== dateFilter) return false;
            return true;
        });
        // Priority weight for tie-breaking
        const pWeight = { critical: 0, high: 1, medium: 2, low: 3 };
        const sWeight = { in_progress: 0, pending: 1, delayed: 2, completed: 3, validated: 4 };
        const cmpPriorityState = (a, b) =>
            (sWeight[a.state] ?? 9) - (sWeight[b.state] ?? 9) ||
            (pWeight[a.priority] ?? 9) - (pWeight[b.priority] ?? 9);

        if (sortMode === "date_asc") {
            return arr.sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date) || cmpPriorityState(a, b));
        }
        if (sortMode === "date_desc") {
            return arr.sort((a, b) => b.scheduled_date.localeCompare(a.scheduled_date) || cmpPriorityState(a, b));
        }
        // smart: today first → upcoming → overdue (oldest first) → done past
        const bucket = (t) => {
            if (t.scheduled_date === todayIso) return 0; // today
            const isPast = t.scheduled_date < todayIso;
            const open = ["pending", "in_progress", "delayed"].includes(t.state);
            if (isPast && open) return 1; // overdue
            if (!isPast) return 2; // future
            return 3; // past done
        };
        return arr.sort((a, b) => {
            const ba = bucket(a);
            const bb = bucket(b);
            if (ba !== bb) return ba - bb;
            // within bucket
            if (ba === 0) return cmpPriorityState(a, b); // today: state then priority
            if (ba === 2) return a.scheduled_date.localeCompare(b.scheduled_date) || cmpPriorityState(a, b);
            // overdue: oldest first (most urgent)
            if (ba === 1) return a.scheduled_date.localeCompare(b.scheduled_date) || cmpPriorityState(a, b);
            // done past: most recent first
            return b.scheduled_date.localeCompare(a.scheduled_date);
        });
    }, [tasks, filter, dateFilter, sortMode, todayIso]);

    const filtered = sorted;
    const todayCount = tasks.filter((t) => t.scheduled_date === todayIso).length;

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.property_id) {
            setError("Selecciona una propiedad");
            return;
        }
        setSaving(true);
        setError("");
        try {
            await api.post("/tasks", {
                ...form,
                estimated_minutes: Number(form.estimated_minutes),
                assigned_to: form.assigned_to || null,
                assigned_to_secondary: form.assigned_to_secondary || null,
                start_time: form.start_time || null,
                end_time: form.end_time || null,
            });
            setOpen(false);
            setForm({
                property_id: "",
                type: "checkout",
                priority: "medium",
                scheduled_date: todayISO(),
                estimated_minutes: 90,
                assigned_to: "",
                assigned_to_secondary: "",
                start_time: "",
                end_time: "",
                key_access: "",
                service_type_text: "",
                day_instructions: "",
                notes: "",
            });
            load();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const states = meta?.task_states || Object.keys(TASK_STATE_LABELS);
    const types = meta?.task_types || Object.keys(TASK_TYPE_LABELS);
    const priorities = meta?.incident_priorities || Object.keys(PRIORITY_LABELS);

    // Bulk selection helpers
    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const selectAllVisible = () => setSelectedIds(new Set(filtered.map((t) => t.id)));
    const clearSelection = () => setSelectedIds(new Set());

    const bulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`¿Eliminar ${selectedIds.size} tareas de limpieza? Esta acción es definitiva.`)) return;
        setBulkBusy(true);
        try {
            await api.post("/tasks/bulk-delete", { task_ids: Array.from(selectedIds) });
            clearSelection();
            load();
        } catch (err) {
            alert(formatApiError(err));
        } finally {
            setBulkBusy(false);
        }
    };

    const bulkAssign = async (employeeId) => {
        if (selectedIds.size === 0 || !employeeId) return;
        setBulkBusy(true);
        try {
            await api.post("/tasks/bulk-assign", {
                task_ids: Array.from(selectedIds),
                assigned_to: employeeId,
            });
            clearSelection();
            load();
        } catch (err) {
            alert(formatApiError(err));
        } finally {
            setBulkBusy(false);
        }
    };

    const inlineAssign = async (taskId, employeeId) => {
        try {
            await api.patch(`/tasks/${taskId}`, { assigned_to: employeeId || null });
            setTasks((prev) =>
                prev.map((t) => (t.id === taskId ? { ...t, assigned_to: employeeId || null } : t)),
            );
        } catch (err) {
            alert(formatApiError(err));
        }
    };

    const cleaners = employees.filter(
        (e) => e.active !== false && ["cleaner", "supervisor", "technician"].includes(e.role),
    );

    return (
        <div className="space-y-6" data-testid="cleanings-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Planificación operativa
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                        Limpiezas
                    </h2>
                    <p className="text-sm text-zentra-muted mt-1">
                        {filtered.length} tareas en pantalla · {tasks.length} totales
                    </p>
                </div>
                <button className="zentra-btn-primary" onClick={() => setOpen(true)} data-testid="new-task-button">
                    <Plus size={16} /> Nueva tarea
                </button>
            </div>

            {/* Filters */}
            <div className="zentra-card-static p-3 flex flex-wrap items-center gap-3" data-testid="task-filters">
                <div className="flex items-center gap-2">
                    <CalendarDays size={14} className="text-zentra-muted" />
                    <input
                        type="date"
                        className="zentra-input w-auto"
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        data-testid="task-date-filter"
                    />
                    {dateFilter && (
                        <button onClick={() => setDateFilter("")} className="text-xs text-zentra-muted underline">
                            limpiar
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-1.5 overflow-x-auto">
                    <FilterChip
                        active={dateFilter === todayIso}
                        onClick={() => setDateFilter(dateFilter === todayIso ? "" : todayIso)}
                        label={`Hoy${todayCount ? ` · ${todayCount}` : ""}`}
                        testId="filter-today"
                    />
                    <span className="w-px h-5 bg-zentra-border mx-1" />
                    <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="Todas" />
                    {states.map((s) => (
                        <FilterChip
                            key={s}
                            active={filter === s}
                            onClick={() => setFilter(s)}
                            label={TASK_STATE_LABELS[s]?.label || s}
                            testId={`filter-state-${s}`}
                        />
                    ))}
                </div>
                <div className="ml-auto flex items-center gap-2 text-xs">
                    <span className="text-[10px] tracking-widest uppercase text-zentra-muted">Orden</span>
                    <select
                        className="zentra-select w-auto py-1.5 text-xs"
                        value={sortMode}
                        onChange={(e) => setSortMode(e.target.value)}
                        data-testid="task-sort"
                    >
                        <option value="smart">Hoy primero (inteligente)</option>
                        <option value="date_asc">Fecha ascendente</option>
                        <option value="date_desc">Fecha descendente</option>
                    </select>
                </div>
            </div>

            {/* Bulk action bar — shown only when selection exists */}
            {selectedIds.size > 0 && (
                <div
                    className="zentra-card-static p-3 flex flex-wrap items-center gap-3 sticky top-2 z-20 bg-zentra-accent/5 border-zentra-accent/30"
                    data-testid="bulk-action-bar"
                >
                    <span className="text-sm font-medium text-zentra-ink">
                        {selectedIds.size} {selectedIds.size === 1 ? "tarea seleccionada" : "tareas seleccionadas"}
                    </span>
                    <div className="flex items-center gap-2 ml-auto flex-wrap">
                        <select
                            className="zentra-select w-auto py-1.5 text-xs"
                            defaultValue=""
                            onChange={(e) => {
                                const v = e.target.value;
                                e.target.value = "";
                                if (v) bulkAssign(v);
                            }}
                            disabled={bulkBusy}
                            data-testid="bulk-assign-select"
                        >
                            <option value="">Asignar a…</option>
                            {cleaners.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                        <button
                            onClick={bulkDelete}
                            disabled={bulkBusy}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-red-200 bg-white text-red-700 text-xs font-semibold hover:bg-red-50"
                            data-testid="bulk-delete-button"
                        >
                            <Trash2 size={13} /> Eliminar {selectedIds.size}
                        </button>
                        <button onClick={clearSelection} className="zentra-btn-ghost text-xs" data-testid="bulk-clear-button">
                            Limpiar selección
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="zentra-skeleton h-80" />
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={Sparkles}
                    title="Sin tareas"
                    description="Programa la primera limpieza para este día."
                    action={
                        <button onClick={() => setOpen(true)} className="zentra-btn-primary mt-2">
                            <Plus size={14} /> Nueva tarea
                        </button>
                    }
                />
            ) : (
                <div className="zentra-card-static overflow-hidden">
                    <table className="zentra-table" data-testid="task-table">
                        <thead>
                            <tr>
                                <th className="w-8">
                                    <input
                                        type="checkbox"
                                        className="w-3.5 h-3.5 accent-zentra-primary cursor-pointer"
                                        checked={filtered.length > 0 && selectedIds.size === filtered.length}
                                        onChange={(e) =>
                                            e.target.checked ? selectAllVisible() : clearSelection()
                                        }
                                        title="Seleccionar todas"
                                        data-testid="bulk-select-all"
                                    />
                                </th>
                                <th className="w-32">Fecha</th>
                                <th>Propiedad</th>
                                <th>Tipo</th>
                                <th>Prioridad</th>
                                <th>Asignado</th>
                                <th>Estado</th>
                                <th className="text-right">Duración</th>
                            </tr>
                        </thead>
                        <tbody>
                            {renderTaskRowsGrouped(filtered, propMap, empMap, todayIso, sortMode, {
                                selectedIds,
                                toggleSelect,
                                cleaners,
                                inlineAssign,
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title="Nueva tarea de limpieza"
                subtitle="Asigna una limpieza programada con checklist automática."
                wide
                testId="task-modal"
                footer={
                    <>
                        <button type="button" className="zentra-btn-ghost" onClick={() => setOpen(false)}>
                            Cancelar
                        </button>
                        <button type="submit" form="task-form" className="zentra-btn-primary" disabled={saving} data-testid="save-task">
                            {saving ? "Creando…" : "Crear tarea"}
                        </button>
                    </>
                }
            >
                <form id="task-form" onSubmit={submit} className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <label className="zentra-label">Propiedad</label>
                        <select
                            className="zentra-select"
                            required
                            value={form.property_id}
                            onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                            data-testid="task-property"
                        >
                            <option value="">Seleccionar propiedad…</option>
                            {properties.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.code} · {p.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="zentra-label">Tipo</label>
                        <select
                            className="zentra-select"
                            value={form.type}
                            onChange={(e) => setForm({ ...form, type: e.target.value })}
                            data-testid="task-type"
                        >
                            {types.map((t) => (
                                <option key={t} value={t}>
                                    {TASK_TYPE_LABELS[t] || t}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="zentra-label">Prioridad</label>
                        <select
                            className="zentra-select"
                            value={form.priority}
                            onChange={(e) => setForm({ ...form, priority: e.target.value })}
                        >
                            {priorities.map((p) => (
                                <option key={p} value={p}>
                                    {PRIORITY_LABELS[p]?.label || p}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="zentra-label">Fecha programada</label>
                        <input
                            type="date"
                            className="zentra-input"
                            value={form.scheduled_date}
                            onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                            data-testid="task-date"
                        />
                    </div>
                    <div>
                        <label className="zentra-label">Duración estimada (min)</label>
                        <input
                            type="number"
                            min="15"
                            className="zentra-input"
                            value={form.estimated_minutes}
                            onChange={(e) => setForm({ ...form, estimated_minutes: e.target.value })}
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Asignar a</label>
                        <select
                            className="zentra-select"
                            value={form.assigned_to}
                            onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                            data-testid="task-assignee"
                        >
                            <option value="">Sin asignar</option>
                            {employees.map((e) => (
                                <option key={e.id} value={e.id}>
                                    {e.name} · {e.role}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Notas</label>
                        <textarea
                            className="zentra-textarea min-h-[70px]"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Instrucciones específicas…"
                        />
                    </div>
                    {error && (
                        <div className="col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
                            {error}
                        </div>
                    )}
                </form>
            </Modal>
        </div>
    );
}

function FilterChip({ active, onClick, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition border ${
                active
                    ? "bg-zentra-primary text-white border-zentra-primary"
                    : "bg-white text-zentra-ink border-zentra-border hover:border-zentra-olive"
            }`}
            data-testid={testId}
        >
            {label}
        </button>
    );
}

function GroupHeader({ label, count, testId, accent, colSpan = 7 }) {
    return (
        <tr className="bg-zentra-bg/70 border-y border-zentra-border" data-testid={testId}>
            <td colSpan={colSpan} className="!py-2 !px-4">
                <div className="flex items-center gap-2">
                    <span
                        className={`text-[10px] font-semibold tracking-widest uppercase ${
                            accent === "critical"
                                ? "text-red-700"
                                : accent === "today"
                                  ? "text-zentra-accent-strong"
                                  : "text-zentra-muted"
                        }`}
                    >
                        {label}
                    </span>
                    <span className="text-[10px] font-display font-bold text-zentra-ink">{count}</span>
                </div>
            </td>
        </tr>
    );
}

function TaskRow({ task, prop, emp, isToday, isOverdue, selectedIds, toggleSelect, cleaners, inlineAssign }) {
    const isSelected = selectedIds?.has(task.id);
    return (
        <tr
            className={`${
                isSelected
                    ? "bg-zentra-accent/15 hover:bg-zentra-accent/20"
                    : isToday
                      ? "bg-zentra-accent/5 hover:bg-zentra-accent/10"
                      : isOverdue
                        ? "bg-red-50/40 hover:bg-red-50"
                        : "hover:bg-zentra-bg/30"
            }`}
            data-testid={`task-row-${task.id}`}
        >
            <td className="w-8">
                {toggleSelect && (
                    <input
                        type="checkbox"
                        className="w-3.5 h-3.5 accent-zentra-primary cursor-pointer"
                        checked={!!isSelected}
                        onChange={() => toggleSelect(task.id)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`task-select-${task.id}`}
                    />
                )}
            </td>
            <td className="font-mono-zentra text-xs">
                <div className="flex items-center gap-2">
                    {isToday && (
                        <span className="w-1.5 h-1.5 rounded-full bg-zentra-accent" title="Hoy" />
                    )}
                    <span className={isOverdue ? "text-red-700 font-semibold" : ""}>
                        {task.scheduled_date}
                    </span>
                </div>
            </td>
            <td>
                <Link
                    to={`/limpiezas/${task.id}`}
                    className="font-medium text-zentra-ink hover:text-zentra-primary"
                >
                    {prop?.name || "—"}
                </Link>
                <span className="block text-[11px] text-zentra-muted font-mono-zentra">
                    {prop?.code || ""}
                </span>
            </td>
            <td>{TASK_TYPE_LABELS[task.type] || task.type}</td>
            <td>
                <StateBadge value={task.priority} dict={PRIORITY_LABELS} />
            </td>
            <td className="text-sm">
                {inlineAssign && cleaners ? (
                    <select
                        className="border border-zentra-border bg-white text-xs px-1.5 py-1 rounded-md outline-none hover:border-zentra-primary focus:border-zentra-primary focus:ring-2 focus:ring-zentra-accent/30 max-w-[180px]"
                        value={task.assigned_to || ""}
                        onChange={(e) => inlineAssign(task.id, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`inline-assign-${task.id}`}
                    >
                        <option value="">— Sin asignar —</option>
                        {cleaners.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                ) : (
                    <span className="text-zentra-muted">{emp?.name || "Sin asignar"}</span>
                )}
            </td>
            <td>
                <StateBadge value={task.state} dict={TASK_STATE_LABELS} />
            </td>
            <td className="text-right">
                <span className="inline-flex items-center gap-1 text-xs text-zentra-muted">
                    <Clock size={11} /> {task.estimated_minutes}'
                </span>
            </td>
        </tr>
    );
}

function renderTaskRowsGrouped(tasks, propMap, empMap, todayIso, sortMode, ctx = {}) {
    // When user picks an explicit sort, skip grouping
    if (sortMode !== "smart") {
        return tasks.map((t) => (
            <TaskRow
                key={t.id}
                task={t}
                prop={propMap[t.property_id]}
                emp={empMap[t.assigned_to]}
                isToday={t.scheduled_date === todayIso}
                isOverdue={
                    t.scheduled_date < todayIso &&
                    ["pending", "in_progress", "delayed"].includes(t.state)
                }
                {...ctx}
            />
        ));
    }

    // Smart mode: group by Hoy / Retrasadas / Próximas / Histórico
    const groups = { today: [], overdue: [], upcoming: [], history: [] };
    for (const t of tasks) {
        if (t.scheduled_date === todayIso) groups.today.push(t);
        else if (t.scheduled_date < todayIso && ["pending", "in_progress", "delayed"].includes(t.state))
            groups.overdue.push(t);
        else if (t.scheduled_date >= todayIso) groups.upcoming.push(t);
        else groups.history.push(t);
    }

    const rows = [];
    if (groups.today.length > 0) {
        rows.push(
            <GroupHeader key="g-today" label="Hoy · en curso" count={groups.today.length} testId="group-today" accent="today" colSpan={8} />,
        );
        for (const t of groups.today) {
            rows.push(
                <TaskRow key={t.id} task={t} prop={propMap[t.property_id]} emp={empMap[t.assigned_to]} isToday={true} isOverdue={false} {...ctx} />,
            );
        }
    }
    if (groups.overdue.length > 0) {
        rows.push(
            <GroupHeader key="g-overdue" label="Retrasadas" count={groups.overdue.length} testId="group-overdue" accent="critical" colSpan={8} />,
        );
        for (const t of groups.overdue) {
            rows.push(
                <TaskRow key={t.id} task={t} prop={propMap[t.property_id]} emp={empMap[t.assigned_to]} isToday={false} isOverdue={true} {...ctx} />,
            );
        }
    }
    if (groups.upcoming.length > 0) {
        rows.push(
            <GroupHeader key="g-upcoming" label="Próximas" count={groups.upcoming.length} testId="group-upcoming" colSpan={8} />,
        );
        for (const t of groups.upcoming) {
            rows.push(
                <TaskRow key={t.id} task={t} prop={propMap[t.property_id]} emp={empMap[t.assigned_to]} isToday={false} isOverdue={false} {...ctx} />,
            );
        }
    }
    if (groups.history.length > 0) {
        rows.push(
            <GroupHeader key="g-history" label="Histórico" count={groups.history.length} testId="group-history" colSpan={8} />,
        );
        for (const t of groups.history) {
            rows.push(
                <TaskRow key={t.id} task={t} prop={propMap[t.property_id]} emp={empMap[t.assigned_to]} isToday={false} isOverdue={false} {...ctx} />,
            );
        }
    }
    return rows;
}
